import { AIRPORTS, STARTER_ICAOS } from "./airports";
import { jetATableAmount, lookupJetA, type JetAOverride } from "./jetA";
import type { CostModel, MxCostModel, RateTables, SceneryAirport } from "./types";

/** Fallback amounts when rate tables are not passed — keep aligned with ledger defaults. */
const FALLBACK_COSTS: CostModel = { fuelCadPerGal: 8.5, handlingCad: 450, landingCad: 180 };
const FALLBACK_MX: MxCostModel = { laborCad: 8500, partsCad: 4200, inspectionCad: 2800 };

/**
 * Company home / ledger / quote currency. CAD is Emerson’s NS-ops default.
 * Amounts in catalogs and the ledger are home-denominated. Foreign figures
 * need an explicit FX conversion (`currency.ts`) — never a silent mix.
 */
export type CurrencyCode = "CAD" | "USD" | "EUR" | "GBP";

export const DEFAULT_CURRENCY: CurrencyCode = "CAD";

export const CURRENCY_OPTIONS: { code: CurrencyCode; label: string }[] = [
  { code: "CAD", label: "Canadian dollar" },
  { code: "USD", label: "US dollar" },
  { code: "EUR", label: "Euro" },
  { code: "GBP", label: "Pound sterling" },
];

export type FuelKind = "jeta" | "avgas";

export type FuelCatalogRow = {
  id: string;
  icao: string;
  fuel: FuelKind;
  pricePerGal: number;
  notes: string;
};

export type MxCatalogKind = "labor" | "parts" | "inspection";
export type MxCatalogUnit = "hour" | "flat" | "each";

export type MxCatalogRow = {
  id: string;
  /** ICAO type (H25B / PC12) or `generic`. */
  aircraftClass: string;
  /** Display name — Hawker 800XP rows are required where Mx applies. */
  airframeName: string;
  kind: MxCatalogKind;
  unit: MxCatalogUnit;
  amount: number;
  notes: string;
};

export type OpsFeeKind = "landing" | "parking" | "handling";

/**
 * Airport / FBO fee planning class. Hawker 800XP (MTOW 28000 lb) is midsize.
 * Not Citation-blind: light ≠ midsize ≠ heavy. Overrideable per ICAO.
 */
export type OpsWeightClass = "light" | "midsize" | "heavy";

export const OPS_WEIGHT_CLASS_ICAO: Record<OpsWeightClass, string> = {
  light: "LIGHT",
  midsize: "MIDSIZE",
  heavy: "HEAVY",
};

export type OpsCatalogRow = {
  id: string;
  /** `*` company default; LIGHT / MIDSIZE / HEAVY weight-class; else ICAO override. */
  icao: string;
  kind: OpsFeeKind;
  amount: number;
  notes: string;
};

export type ExtraKind = "transport" | "catering_std" | "catering_special" | "gpu" | "lav" | "deice";

export type ExtraCatalogRow = {
  id: string;
  kind: ExtraKind;
  label: string;
  amount: number;
  /** GPU / lav / deice start as stubs for a later extras form. */
  stub: boolean;
  notes: string;
};

/** Desk-owned pricing truth. Optional APIs may later fill helpers — never sole truth. */
export type MoneyCatalogs = {
  currency: CurrencyCode;
  fuel: FuelCatalogRow[];
  mx: MxCatalogRow[];
  ops: OpsCatalogRow[];
  extras: ExtraCatalogRow[];
};

export const DEFAULT_ICAO = "*";
export const GENERIC_MX_CLASS = "generic";
export const HAWKER_MX_CLASS = "H25B";
export const PC12_MX_CLASS = "PC12";
export const HAWKER_800XP_NAME = "Hawker 800XP";

export const FUEL_KIND_LABEL: Record<FuelKind, string> = {
  jeta: "Jet-A",
  avgas: "Avgas",
};

export const EXTRA_KIND_LABEL: Record<ExtraKind, string> = {
  transport: "Ground transport",
  catering_std: "Catering (standard)",
  catering_special: "Catering (special)",
  gpu: "GPU",
  lav: "Lav service",
  deice: "Deice / anti-ice",
};

const AVGAS_SEED_ICAOS = ["CYPD", "CYQY", "CYYG", "CYQM", "KBGR", "KACK"] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function isCurrencyCode(value: unknown): value is CurrencyCode {
  return value === "CAD" || value === "USD" || value === "EUR" || value === "GBP";
}

export function hydrateCurrency(raw?: unknown): CurrencyCode {
  return isCurrencyCode(raw) ? raw : DEFAULT_CURRENCY;
}

const CURRENCY_LOCALE: Record<CurrencyCode, string> = {
  CAD: "en-CA",
  USD: "en-US",
  EUR: "en-IE",
  GBP: "en-GB",
};

/** Format an amount already in `currency` (home, or a labeled foreign original). */
export function formatMoney(amount: number, currency: CurrencyCode = DEFAULT_CURRENCY): string {
  const abs = Math.abs(amount).toLocaleString(CURRENCY_LOCALE[currency] ?? "en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const symbol = currency === "EUR" ? "€" : currency === "GBP" ? "£" : "$";
  return amount < 0 ? `−${symbol}${abs}` : `${symbol}${abs}`;
}

export function moneyUnitLabel(currency: CurrencyCode, suffix: string): string {
  return `${currency} / ${suffix}`;
}

function newRowId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

export function fuelRowId(icao: string, fuel: FuelKind): string {
  return `fuel-${icao.toUpperCase()}-${fuel}`;
}

export function mxRowId(aircraftClass: string, kind: MxCatalogKind, unit: MxCatalogUnit, airframeName?: string): string {
  const slug = (airframeName ?? aircraftClass).toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return `mx-${aircraftClass.toUpperCase()}-${kind}-${unit}-${slug}`;
}

export function opsRowId(icao: string, kind: OpsFeeKind): string {
  return `ops-${icao.toUpperCase()}-${kind}`;
}

export function extraRowId(kind: ExtraKind, label: string): string {
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || kind;
  return `extra-${kind}-${slug}`;
}

/**
 * Fuel / fee seed sources (operator-sim planning — not a live market API):
 *
 * Jet-A / Avgas
 * - Desk default 8.50 home / US gal is an Atlantic / Canada FBO planning band
 *   (higher than US contract Jet-A). Not a posted FBO invoice.
 * - Jet-A regional / ICAO deltas come from `jetA.ts` (CAD/USD curated table).
 *   Avgas stays +0.75 on the default. See docs/JET_A.md.
 * - Hawker 800XP planning density 6.7 lb/US gal (Emerson desk + Raytheon Spec
 *   2004 usable-fuel note). Catalog prices stay US gal; arrival may enter kg.
 * - Typical burn ballpark ~217 USGPH (Jetcraft 800XP overview) informs hours
 *   → gallons on the rate card — not AFM.
 *
 * Landing / handling / parking
 * - `*` / MIDSIZE = Hawker 800XP (MTOW 28,000 lb) midsize-jet DOC-style FBO
 *   band: landing ~180, handling ~450, parking ~80 (home). Published light-jet
 *   / midsize planning sheets (Conklin-style operator DOC ranges, public
 *   broker cost summaries) — not Citation CJ3/M2 defaults and not NAV CANADA
 *   or a specific FBO tariff copied as truth.
 * - LIGHT (PC-12 / <12,500 lb) ~0.55× midsize. HEAVY ~1.8×.
 * - ICAO overrides (CYPD cheaper pad; KTEB / KHPN / KMIA premium; CYYZ / CYUL
 *   / KBOS / KJFK metro) are the same planning band.
 *
 * Every seeded number is a table the operator can edit. Measured burns and
 * posted ledger lines win over the table when present.
 */
function regionFuel(icao: string, fuel: FuelKind, fallback: number): number {
  if (fuel === "avgas") return Math.round((fallback + 0.75) * 100) / 100;
  return jetATableAmount(icao, "CAD", fallback);
}

function knownIcaos(extra: string[] = []): string[] {
  const set = new Set<string>([...STARTER_ICAOS, ...AIRPORTS.map((a) => a.icao), ...extra]);
  return [...set].filter(Boolean).sort();
}

export function seedFuelCatalog(opts: { icaos?: string[]; costs?: CostModel } = {}): FuelCatalogRow[] {
  const jet = opts.costs?.fuelCadPerGal ?? FALLBACK_COSTS.fuelCadPerGal;
  const icaos = opts.icaos?.length ? [...new Set(opts.icaos.map((c) => c.toUpperCase()))].sort() : knownIcaos();
  const rows: FuelCatalogRow[] = [
    {
      id: fuelRowId(DEFAULT_ICAO, "jeta"),
      icao: DEFAULT_ICAO,
      fuel: "jeta",
      pricePerGal: jet,
      notes: "Company default Jet-A (Atlantic / Canada FBO planning band, home / US gal). Hawker OEM planning density 6.7 lb/US gal — Emerson desk + Raytheon Spec 2004. Not a live fuel API.",
    },
    {
      id: fuelRowId(DEFAULT_ICAO, "avgas"),
      icao: DEFAULT_ICAO,
      fuel: "avgas",
      pricePerGal: Math.round((jet + 0.75) * 100) / 100,
      notes: "Company default Avgas",
    },
  ];
  for (const icao of icaos) {
    if (icao === DEFAULT_ICAO) continue;
    rows.push({
      id: fuelRowId(icao, "jeta"),
      icao,
      fuel: "jeta",
      pricePerGal: regionFuel(icao, "jeta", jet),
      notes: "",
    });
    if ((AVGAS_SEED_ICAOS as readonly string[]).includes(icao)) {
      rows.push({
        id: fuelRowId(icao, "avgas"),
        icao,
        fuel: "avgas",
        pricePerGal: regionFuel(icao, "avgas", jet),
        notes: "",
      });
    }
  }
  return rows;
}

export function seedMxCatalog(mx: MxCostModel = FALLBACK_MX): MxCatalogRow[] {
  const hawkerLaborFlat = mx.laborCad;
  const hawkerParts = mx.partsCad;
  const hawkerInsp = mx.inspectionCad;
  return [
    {
      id: mxRowId(HAWKER_MX_CLASS, "labor", "flat", HAWKER_800XP_NAME),
      aircraftClass: HAWKER_MX_CLASS,
      airframeName: HAWKER_800XP_NAME,
      kind: "labor",
      unit: "flat",
      amount: hawkerLaborFlat,
      notes: "Close-mx labor — Hawker 800XP class",
    },
    {
      id: mxRowId(HAWKER_MX_CLASS, "labor", "hour", HAWKER_800XP_NAME),
      aircraftClass: HAWKER_MX_CLASS,
      airframeName: HAWKER_800XP_NAME,
      kind: "labor",
      unit: "hour",
      amount: 185,
      notes: "Estimate hook — labor / hour",
    },
    {
      id: mxRowId(HAWKER_MX_CLASS, "parts", "flat", HAWKER_800XP_NAME),
      aircraftClass: HAWKER_MX_CLASS,
      airframeName: HAWKER_800XP_NAME,
      kind: "parts",
      unit: "flat",
      amount: hawkerParts,
      notes: "Typical inspection parts kit",
    },
    {
      id: mxRowId(HAWKER_MX_CLASS, "inspection", "flat", HAWKER_800XP_NAME),
      aircraftClass: HAWKER_MX_CLASS,
      airframeName: HAWKER_800XP_NAME,
      kind: "inspection",
      unit: "flat",
      amount: hawkerInsp,
      notes: "400-hr / phase inspection (FlightFX 800XP B/C/D)",
    },
    {
      id: mxRowId(PC12_MX_CLASS, "labor", "flat", "Pilatus PC-12 NG"),
      aircraftClass: PC12_MX_CLASS,
      airframeName: "Pilatus PC-12 NG",
      kind: "labor",
      unit: "flat",
      amount: Math.round(hawkerLaborFlat * 0.55),
      notes: "Close-mx labor — PC-12 class",
    },
    {
      id: mxRowId(PC12_MX_CLASS, "labor", "hour", "Pilatus PC-12 NG"),
      aircraftClass: PC12_MX_CLASS,
      airframeName: "Pilatus PC-12 NG",
      kind: "labor",
      unit: "hour",
      amount: 145,
      notes: "Estimate hook — labor / hour",
    },
    {
      id: mxRowId(PC12_MX_CLASS, "parts", "flat", "Pilatus PC-12 NG"),
      aircraftClass: PC12_MX_CLASS,
      airframeName: "Pilatus PC-12 NG",
      kind: "parts",
      unit: "flat",
      amount: Math.round(hawkerParts * 0.5),
      notes: "",
    },
    {
      id: mxRowId(PC12_MX_CLASS, "inspection", "flat", "Pilatus PC-12 NG"),
      aircraftClass: PC12_MX_CLASS,
      airframeName: "Pilatus PC-12 NG",
      kind: "inspection",
      unit: "flat",
      amount: Math.round(hawkerInsp * 0.55),
      notes: "",
    },
    {
      id: mxRowId(GENERIC_MX_CLASS, "labor", "flat", "Other class"),
      aircraftClass: GENERIC_MX_CLASS,
      airframeName: "Other class",
      kind: "labor",
      unit: "flat",
      amount: Math.round(hawkerLaborFlat * 0.45),
      notes: "Fallback when the tail class has no row",
    },
    {
      id: mxRowId(GENERIC_MX_CLASS, "parts", "flat", "Other class"),
      aircraftClass: GENERIC_MX_CLASS,
      airframeName: "Other class",
      kind: "parts",
      unit: "flat",
      amount: Math.round(hawkerParts * 0.4),
      notes: "",
    },
    {
      id: mxRowId(GENERIC_MX_CLASS, "inspection", "flat", "Other class"),
      aircraftClass: GENERIC_MX_CLASS,
      airframeName: "Other class",
      kind: "inspection",
      unit: "flat",
      amount: Math.round(hawkerInsp * 0.4),
      notes: "",
    },
  ];
}

function opsAmount(kind: OpsFeeKind, icao: string, costs: CostModel): number {
  const base = kind === "landing" ? costs.landingCad : kind === "handling" ? costs.handlingCad : Math.round(costs.landingCad * 0.45);
  const code = icao.toUpperCase();
  if (code === DEFAULT_ICAO || code === "MIDSIZE") return base;
  if (code === "LIGHT") return Math.round(base * 0.55);
  if (code === "HEAVY") return Math.round(base * 1.8);
  if (code === "CYPD") return Math.round(base * 0.55);
  if (["KTEB", "KHPN", "KMIA"].includes(code)) return Math.round(base * 1.55);
  if (["CYYZ", "CYUL", "KBOS", "KJFK"].includes(code)) return Math.round(base * 1.25);
  if (code.startsWith("C")) return Math.round(base * 0.9);
  return base;
}

const WEIGHT_CLASS_NOTES: Record<OpsWeightClass, string> = {
  light:
    "Weight-class default — light / turboprop (<12,500 lb MTOW, PC-12). ~0.55× Hawker midsize. Not a Citation CJ default.",
  midsize:
    "Weight-class default — midsize jet. Hawker 800XP MTOW 28,000 lb (Emerson desk). Same band as company `*`.",
  heavy: "Weight-class default — heavy jet (~1.8× Hawker midsize). Planning band, not a slot/FBO invoice.",
};

/** Hawker 800XP (H25B) is midsize. PC-12 / pistons are light. Large-cabin G/C types are heavy. */
export function weightClassForType(icaoType?: string): OpsWeightClass {
  const t = (icaoType ?? "").toUpperCase();
  if (t === "PC12" || t.startsWith("C172") || t.startsWith("PA") || t.includes("TBM") || t.startsWith("C90")) {
    return "light";
  }
  if (t.startsWith("G65") || t.startsWith("G6") || t.startsWith("GLF") || t.startsWith("C75") || t.startsWith("C56") || t.includes("GLOBAL")) {
    return "heavy";
  }
  return "midsize";
}

export function seedOpsCatalog(opts: { icaos?: string[]; costs?: CostModel } = {}): OpsCatalogRow[] {
  const costs = opts.costs ?? FALLBACK_COSTS;
  const overrides = opts.icaos?.length
    ? [...new Set(opts.icaos.map((c) => c.toUpperCase()))].sort()
    : [...STARTER_ICAOS];
  const kinds: OpsFeeKind[] = ["landing", "parking", "handling"];
  const rows: OpsCatalogRow[] = [];
  for (const kind of kinds) {
    rows.push({
      id: opsRowId(DEFAULT_ICAO, kind),
      icao: DEFAULT_ICAO,
      kind,
      amount: opsAmount(kind, DEFAULT_ICAO, costs),
      notes:
        "Company default (Hawker 800XP midsize planning) — used when an airport has no override. Light-jet DOC band, not Citation-blind and not a live tariff.",
    });
  }
  for (const cls of ["light", "midsize", "heavy"] as const) {
    const token = OPS_WEIGHT_CLASS_ICAO[cls];
    for (const kind of kinds) {
      rows.push({
        id: opsRowId(token, kind),
        icao: token,
        kind,
        amount: opsAmount(kind, token, costs),
        notes: WEIGHT_CLASS_NOTES[cls],
      });
    }
  }
  for (const icao of overrides) {
    if (icao === DEFAULT_ICAO) continue;
    for (const kind of kinds) {
      rows.push({
        id: opsRowId(icao, kind),
        icao,
        kind,
        amount: opsAmount(kind, icao, costs),
        notes: "",
      });
    }
  }
  return rows;
}

export function seedExtrasCatalog(): ExtraCatalogRow[] {
  return [
    {
      id: extraRowId("transport", "Ground transport — sedan"),
      kind: "transport",
      label: "Ground transport — sedan",
      amount: 180,
      stub: false,
      notes: "",
    },
    {
      id: extraRowId("transport", "Ground transport — van"),
      kind: "transport",
      label: "Ground transport — van",
      amount: 260,
      stub: false,
      notes: "",
    },
    {
      id: extraRowId("catering_std", "Catering — standard"),
      kind: "catering_std",
      label: "Catering — standard",
      amount: 95,
      stub: false,
      notes: "",
    },
    {
      id: extraRowId("catering_special", "Catering — special"),
      kind: "catering_special",
      label: "Catering — special",
      amount: 275,
      stub: false,
      notes: "",
    },
    {
      id: extraRowId("gpu", "GPU"),
      kind: "gpu",
      label: "GPU",
      amount: 0,
      stub: true,
      notes: "Stub — later extras form",
    },
    {
      id: extraRowId("lav", "Lav service"),
      kind: "lav",
      label: "Lav service",
      amount: 0,
      stub: true,
      notes: "Stub — later extras form",
    },
    {
      id: extraRowId("deice", "Deice / anti-ice"),
      kind: "deice",
      label: "Deice / anti-ice",
      amount: 0,
      stub: true,
      notes: "Stub — later extras form",
    },
  ];
}

export function seedMoneyCatalogs(opts: { icaos?: string[]; rates?: RateTables; currency?: CurrencyCode } = {}): MoneyCatalogs {
  const costs = opts.rates?.costs ?? FALLBACK_COSTS;
  const mx = opts.rates?.mx ?? FALLBACK_MX;
  return {
    currency: hydrateCurrency(opts.currency),
    fuel: seedFuelCatalog({ icaos: opts.icaos, costs }),
    mx: seedMxCatalog(mx),
    ops: seedOpsCatalog({ icaos: opts.icaos ?? [...STARTER_ICAOS], costs }),
    extras: seedExtrasCatalog(),
  };
}

function num(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : fallback;
}

function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function hydrateFuelRow(raw: unknown, fallback: FuelCatalogRow): FuelCatalogRow {
  if (!isRecord(raw)) return fallback;
  const fuel: FuelKind = raw.fuel === "avgas" ? "avgas" : "jeta";
  const icao = str(raw.icao, fallback.icao).toUpperCase() || fallback.icao;
  return {
    id: str(raw.id, fallback.id) || fuelRowId(icao, fuel),
    icao,
    fuel,
    pricePerGal: num(raw.pricePerGal, fallback.pricePerGal),
    notes: str(raw.notes, fallback.notes),
  };
}

function hydrateMxRow(raw: unknown, fallback?: MxCatalogRow): MxCatalogRow {
  const kind: MxCatalogKind =
    isRecord(raw) && (raw.kind === "parts" || raw.kind === "inspection" || raw.kind === "labor")
      ? raw.kind
      : (fallback?.kind ?? "labor");
  const unit: MxCatalogUnit =
    isRecord(raw) && (raw.unit === "hour" || raw.unit === "each" || raw.unit === "flat")
      ? raw.unit
      : (fallback?.unit ?? "flat");
  const aircraftClass = (isRecord(raw) ? str(raw.aircraftClass, fallback?.aircraftClass ?? GENERIC_MX_CLASS) : GENERIC_MX_CLASS)
    .trim()
    .toUpperCase();
  const airframeName = isRecord(raw)
    ? str(raw.airframeName, fallback?.airframeName ?? aircraftClass)
    : (fallback?.airframeName ?? aircraftClass);
  return {
    id: isRecord(raw) ? str(raw.id, fallback?.id ?? mxRowId(aircraftClass, kind, unit, airframeName)) : mxRowId(aircraftClass, kind, unit, airframeName),
    aircraftClass,
    airframeName,
    kind,
    unit,
    amount: isRecord(raw) ? num(raw.amount, fallback?.amount ?? 0) : (fallback?.amount ?? 0),
    notes: isRecord(raw) ? str(raw.notes, fallback?.notes ?? "") : "",
  };
}

function hydrateOpsRow(raw: unknown, fallback: OpsCatalogRow): OpsCatalogRow {
  if (!isRecord(raw)) return fallback;
  const kind: OpsFeeKind = raw.kind === "parking" || raw.kind === "handling" || raw.kind === "landing" ? raw.kind : fallback.kind;
  const icao = str(raw.icao, fallback.icao).toUpperCase() || fallback.icao;
  return {
    id: str(raw.id, fallback.id) || opsRowId(icao, kind),
    icao,
    kind,
    amount: num(raw.amount, fallback.amount),
    notes: str(raw.notes, fallback.notes),
  };
}

function hydrateExtraRow(raw: unknown, fallback?: ExtraCatalogRow): ExtraCatalogRow {
  const kind: ExtraKind =
    isRecord(raw) &&
    (raw.kind === "transport" ||
      raw.kind === "catering_std" ||
      raw.kind === "catering_special" ||
      raw.kind === "gpu" ||
      raw.kind === "lav" ||
      raw.kind === "deice")
      ? raw.kind
      : (fallback?.kind ?? "transport");
  const label = isRecord(raw) ? str(raw.label, fallback?.label ?? EXTRA_KIND_LABEL[kind]) : (fallback?.label ?? EXTRA_KIND_LABEL[kind]);
  return {
    id: isRecord(raw) ? str(raw.id, fallback?.id ?? extraRowId(kind, label)) : extraRowId(kind, label),
    kind,
    label,
    amount: isRecord(raw) ? num(raw.amount, fallback?.amount ?? 0) : (fallback?.amount ?? 0),
    stub: isRecord(raw) ? Boolean(raw.stub) : Boolean(fallback?.stub),
    notes: isRecord(raw) ? str(raw.notes, fallback?.notes ?? "") : "",
  };
}

function mergeRequired<T extends { id: string }>(incoming: T[], required: T[]): T[] {
  const ids = new Set(incoming.map((r) => r.id));
  const extra = required.filter((r) => !ids.has(r.id));
  return extra.length ? [...incoming, ...extra] : incoming;
}

/** Pull Wave B `catalogStubs` or a first-class catalogs object. */
export function catalogsFromUnknown(raw?: unknown): Partial<MoneyCatalogs> | undefined {
  if (!isRecord(raw)) return undefined;
  if (isRecord(raw.money)) return catalogsFromUnknown(raw.money);
  if (Array.isArray(raw.fuel) || Array.isArray(raw.mx) || Array.isArray(raw.ops) || Array.isArray(raw.extras) || raw.currency) {
    return raw as Partial<MoneyCatalogs>;
  }
  return undefined;
}

export function hydrateMoneyCatalogs(
  raw?: Partial<MoneyCatalogs> | null,
  opts: { icaos?: string[]; rates?: RateTables; currency?: CurrencyCode } = {},
): MoneyCatalogs {
  const seeded = seedMoneyCatalogs(opts);
  if (!raw || typeof raw !== "object") return seeded;
  const fuelIn = Array.isArray(raw.fuel) ? raw.fuel.map((row, i) => hydrateFuelRow(row, seeded.fuel[i] ?? seeded.fuel[0]!)) : [];
  const mxIn = Array.isArray(raw.mx) ? raw.mx.map((row) => hydrateMxRow(row)) : [];
  const opsIn = Array.isArray(raw.ops) ? raw.ops.map((row, i) => hydrateOpsRow(row, seeded.ops[i] ?? seeded.ops[0]!)) : [];
  const extrasIn = Array.isArray(raw.extras) ? raw.extras.map((row) => hydrateExtraRow(row)) : [];
  const requiredFuel = seeded.fuel.filter((r) => r.icao === DEFAULT_ICAO);
  const requiredOps = seeded.ops.filter(
    (r) => r.icao === DEFAULT_ICAO || r.icao === "LIGHT" || r.icao === "MIDSIZE" || r.icao === "HEAVY",
  );
  const requiredMx = seeded.mx.filter(
    (r) =>
      (r.aircraftClass === HAWKER_MX_CLASS && r.airframeName.includes("Hawker 800XP") && r.unit === "flat") ||
      (r.aircraftClass === GENERIC_MX_CLASS && r.unit === "flat"),
  );
  const requiredExtras = seeded.extras.filter((r) => r.stub || r.kind === "transport" || r.kind.startsWith("catering"));
  const merged: MoneyCatalogs = {
    currency: hydrateCurrency(raw.currency ?? seeded.currency),
    fuel: fuelIn.length ? mergeRequired(fuelIn, requiredFuel) : seeded.fuel,
    mx: mxIn.length ? mergeRequired(mxIn, requiredMx) : seeded.mx,
    ops: opsIn.length ? mergeRequired(opsIn, requiredOps) : seeded.ops,
    extras: extrasIn.length ? mergeRequired(extrasIn, requiredExtras) : seeded.extras,
  };
  return ensureAirportCatalogRows(merged, opts.icaos ?? [], opts.rates);
}

export function ensureAirportCatalogRows(
  catalogs: MoneyCatalogs,
  icaos: string[],
  rates?: RateTables,
): MoneyCatalogs {
  const costs = rates?.costs ?? FALLBACK_COSTS;
  const ops = [...catalogs.ops];
  const opsKeys = new Set(ops.map((r) => `${r.icao}:${r.kind}`));
  for (const raw of icaos) {
    const icao = raw.trim().toUpperCase();
    if (!icao || icao === DEFAULT_ICAO || icao === "LIGHT" || icao === "MIDSIZE" || icao === "HEAVY") continue;
    for (const kind of ["landing", "parking", "handling"] as const) {
      if (opsKeys.has(`${icao}:${kind}`)) continue;
      ops.push({
        id: opsRowId(icao, kind),
        icao,
        kind,
        amount: opsAmount(kind, icao, costs),
        notes: "Added from Destinations",
      });
      opsKeys.add(`${icao}:${kind}`);
    }
  }
  return { ...catalogs, ops };
}

export function icaosFromScenery(scenery?: SceneryAirport[]): string[] {
  return (scenery ?? []).map((s) => s.icao.toUpperCase()).filter(Boolean);
}

export function fuelKindForType(icaoType?: string): FuelKind {
  const t = (icaoType ?? "").toUpperCase();
  if (t === "H25B" || t === "PC12" || t.includes("JET") || t.startsWith("C5") || t.startsWith("G")) return "jeta";
  if (t.startsWith("C172") || t.startsWith("PA") || t.includes("AVGAS")) return "avgas";
  return "jeta";
}

export function lookupFuelPrice(
  catalogs: MoneyCatalogs | undefined,
  icao: string,
  fuel: FuelKind,
  fallbackPerGal: number,
  jetAOverrides?: readonly JetAOverride[] | null,
): number {
  if (fuel === "avgas") {
    if (!catalogs) return fallbackPerGal;
    const code = icao.trim().toUpperCase();
    const exact = catalogs.fuel.find((r) => r.icao === code && r.fuel === fuel);
    if (exact) return exact.pricePerGal;
    const def = catalogs.fuel.find((r) => r.icao === DEFAULT_ICAO && r.fuel === fuel);
    return def?.pricePerGal ?? fallbackPerGal;
  }
  return lookupJetA({
    icao,
    catalogs,
    overrides: jetAOverrides,
    homeCurrency: catalogs?.currency,
    fallbackPerGal,
  }).pricePerUsGal;
}

export function lookupOpsFee(
  catalogs: MoneyCatalogs | undefined,
  icao: string,
  kind: OpsFeeKind,
  fallback: number,
  weightClass?: OpsWeightClass,
): number {
  if (!catalogs) return fallback;
  const code = icao.trim().toUpperCase();
  const exact = catalogs.ops.find((r) => r.icao === code && r.kind === kind);
  if (exact) return exact.amount;
  if (weightClass) {
    const token = OPS_WEIGHT_CLASS_ICAO[weightClass];
    const byClass = catalogs.ops.find((r) => r.icao === token && r.kind === kind);
    if (byClass) return byClass.amount;
  }
  const def = catalogs.ops.find((r) => r.icao === DEFAULT_ICAO && r.kind === kind);
  return def?.amount ?? fallback;
}

function mxRowMatch(
  catalogs: MoneyCatalogs,
  kind: MxCatalogKind,
  unit: MxCatalogUnit,
  aircraftClass?: string,
  airframeName?: string,
): MxCatalogRow | undefined {
  const cls = (aircraftClass ?? "").trim().toUpperCase();
  const name = (airframeName ?? "").trim().toLowerCase();
  const sameKind = catalogs.mx.filter((r) => r.kind === kind && r.unit === unit);
  if (name.includes("hawker") || name.includes("800xp")) {
    const named = sameKind.find((r) => r.airframeName.toLowerCase().includes("hawker"));
    if (named) return named;
  }
  if (cls) {
    const byClass = sameKind.find((r) => r.aircraftClass === cls);
    if (byClass) return byClass;
  }
  return sameKind.find((r) => r.aircraftClass === GENERIC_MX_CLASS);
}

/** Close-mx / estimate hook: class rows, Hawker 800XP named rows, then generic, then rate-table fallback. */
export function mxCostsForClass(
  catalogs: MoneyCatalogs | undefined,
  input: { icaoType?: string; name?: string; fallback?: MxCostModel } = {},
): MxCostModel {
  const fallback = input.fallback ?? FALLBACK_MX;
  if (!catalogs) return fallback;
  const labor =
    mxRowMatch(catalogs, "labor", "flat", input.icaoType, input.name)?.amount ??
    mxRowMatch(catalogs, "labor", "hour", input.icaoType, input.name)?.amount ??
    fallback.laborCad;
  const parts = mxRowMatch(catalogs, "parts", "flat", input.icaoType, input.name)?.amount ?? fallback.partsCad;
  const inspection = mxRowMatch(catalogs, "inspection", "flat", input.icaoType, input.name)?.amount ?? fallback.inspectionCad;
  return { laborCad: labor, partsCad: parts, inspectionCad: inspection };
}

/** Future Mx estimate form — hours × labor/hr + parts + inspection flats. */
export function mxEstimateFromCatalog(
  catalogs: MoneyCatalogs | undefined,
  input: { icaoType?: string; name?: string; laborHours?: number; fallback?: MxCostModel } = {},
): MxCostModel & { laborHours: number } {
  const base = mxCostsForClass(catalogs, input);
  const hours = input.laborHours && input.laborHours > 0 ? input.laborHours : 0;
  const hourly = catalogs ? mxRowMatch(catalogs, "labor", "hour", input.icaoType, input.name)?.amount : undefined;
  const laborCad = hours > 0 && hourly != null ? Math.round(hours * hourly * 100) / 100 : base.laborCad;
  return { ...base, laborCad, laborHours: hours };
}

export function extrasTotal(catalogs: MoneyCatalogs | undefined, kinds: ExtraKind[]): number {
  if (!catalogs || !kinds.length) return 0;
  let sum = 0;
  for (const kind of kinds) {
    const row = catalogs.extras.find((r) => r.kind === kind && !r.stub);
    if (row) sum += row.amount;
  }
  return Math.round(sum * 100) / 100;
}

export function upsertFuelRow(catalogs: MoneyCatalogs, row: Partial<FuelCatalogRow> & Pick<FuelCatalogRow, "icao" | "fuel">): MoneyCatalogs {
  const icao = row.icao.trim().toUpperCase() || DEFAULT_ICAO;
  const fuel = row.fuel;
  const id = row.id?.trim() || fuelRowId(icao, fuel);
  const next: FuelCatalogRow = {
    id,
    icao,
    fuel,
    pricePerGal: num(row.pricePerGal, 0),
    notes: str(row.notes),
  };
  const idx = catalogs.fuel.findIndex((r) => r.id === id || (r.icao === icao && r.fuel === fuel));
  const fuelRows = [...catalogs.fuel];
  if (idx >= 0) fuelRows[idx] = { ...fuelRows[idx]!, ...next };
  else fuelRows.push(next);
  return { ...catalogs, fuel: fuelRows };
}

export function removeFuelRow(catalogs: MoneyCatalogs, id: string): MoneyCatalogs {
  return { ...catalogs, fuel: catalogs.fuel.filter((r) => r.id !== id) };
}

export function upsertMxRow(catalogs: MoneyCatalogs, row: Partial<MxCatalogRow> & Pick<MxCatalogRow, "aircraftClass" | "kind" | "unit">): MoneyCatalogs {
  const aircraftClass = row.aircraftClass.trim().toUpperCase() || GENERIC_MX_CLASS;
  const airframeName = row.airframeName?.trim() || aircraftClass;
  const id = row.id?.trim() || mxRowId(aircraftClass, row.kind, row.unit, airframeName);
  const next: MxCatalogRow = {
    id,
    aircraftClass,
    airframeName,
    kind: row.kind,
    unit: row.unit,
    amount: num(row.amount, 0),
    notes: str(row.notes),
  };
  const idx = catalogs.mx.findIndex((r) => r.id === id);
  const mx = [...catalogs.mx];
  if (idx >= 0) mx[idx] = { ...mx[idx]!, ...next };
  else mx.push(next);
  return { ...catalogs, mx };
}

export function removeMxRow(catalogs: MoneyCatalogs, id: string): MoneyCatalogs {
  return { ...catalogs, mx: catalogs.mx.filter((r) => r.id !== id) };
}

export function upsertOpsRow(catalogs: MoneyCatalogs, row: Partial<OpsCatalogRow> & Pick<OpsCatalogRow, "icao" | "kind">): MoneyCatalogs {
  const icao = row.icao.trim().toUpperCase() || DEFAULT_ICAO;
  const id = row.id?.trim() || opsRowId(icao, row.kind);
  const next: OpsCatalogRow = {
    id,
    icao,
    kind: row.kind,
    amount: num(row.amount, 0),
    notes: str(row.notes),
  };
  const idx = catalogs.ops.findIndex((r) => r.id === id || (r.icao === icao && r.kind === row.kind));
  const ops = [...catalogs.ops];
  if (idx >= 0) ops[idx] = { ...ops[idx]!, ...next };
  else ops.push(next);
  return { ...catalogs, ops };
}

export function removeOpsRow(catalogs: MoneyCatalogs, id: string): MoneyCatalogs {
  return { ...catalogs, ops: catalogs.ops.filter((r) => r.id !== id) };
}

export function upsertExtraRow(catalogs: MoneyCatalogs, row: Partial<ExtraCatalogRow> & Pick<ExtraCatalogRow, "kind" | "label">): MoneyCatalogs {
  const id = row.id?.trim() || extraRowId(row.kind, row.label);
  const next: ExtraCatalogRow = {
    id,
    kind: row.kind,
    label: row.label.trim() || EXTRA_KIND_LABEL[row.kind],
    amount: num(row.amount, 0),
    stub: Boolean(row.stub),
    notes: str(row.notes),
  };
  const idx = catalogs.extras.findIndex((r) => r.id === id);
  const extras = [...catalogs.extras];
  if (idx >= 0) extras[idx] = { ...extras[idx]!, ...next };
  else extras.push(next);
  return { ...catalogs, extras };
}

export function removeExtraRow(catalogs: MoneyCatalogs, id: string): MoneyCatalogs {
  return { ...catalogs, extras: catalogs.extras.filter((r) => r.id !== id) };
}

export function setCatalogCurrency(catalogs: MoneyCatalogs, currency: CurrencyCode): MoneyCatalogs {
  return { ...catalogs, currency: hydrateCurrency(currency) };
}

export function catalogsAsStubs(catalogs: MoneyCatalogs): Record<string, unknown> {
  return { money: catalogs };
}

export function newFuelDraft(icao = "", fuel: FuelKind = "jeta"): FuelCatalogRow {
  const code = icao.trim().toUpperCase() || DEFAULT_ICAO;
  return {
    id: newRowId(`fuel-${code}-${fuel}`),
    icao: code,
    fuel,
    pricePerGal: FALLBACK_COSTS.fuelCadPerGal,
    notes: "",
  };
}

export function newOpsDraft(icao = "", kind: OpsFeeKind = "landing"): OpsCatalogRow {
  const code = icao.trim().toUpperCase() || DEFAULT_ICAO;
  return {
    id: newRowId(`ops-${code}-${kind}`),
    icao: code,
    kind,
    amount: kind === "handling" ? FALLBACK_COSTS.handlingCad : FALLBACK_COSTS.landingCad,
    notes: "",
  };
}

export function newMxDraft(aircraftClass = HAWKER_MX_CLASS, kind: MxCatalogKind = "labor"): MxCatalogRow {
  const cls = aircraftClass.trim().toUpperCase() || GENERIC_MX_CLASS;
  const airframeName = cls === HAWKER_MX_CLASS ? HAWKER_800XP_NAME : cls === PC12_MX_CLASS ? "Pilatus PC-12 NG" : "Other class";
  return {
    id: newRowId(`mx-${cls}-${kind}`),
    aircraftClass: cls,
    airframeName,
    kind,
    unit: "flat",
    amount: 0,
    notes: "",
  };
}

export function newExtraDraft(kind: ExtraKind = "transport"): ExtraCatalogRow {
  return {
    id: newRowId(`extra-${kind}`),
    kind,
    label: EXTRA_KIND_LABEL[kind],
    amount: 0,
    stub: kind === "gpu" || kind === "lav" || kind === "deice",
    notes: "",
  };
}
